import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CameraIcon, InfoIcon, MapPinIcon } from 'lucide-react';
import { EntryLayout } from '../../components/EntryLayout';
import { Button } from '../../components/ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
import { useSession } from '../../contexts/SessionContext';

/**
 * MOB/Onboarding/Permissions — explains before asking, never forces.
 * Denied permissions fall back to the Phase 4 permission states.
 */
export function OnboardingPermissions() {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  const {
    permissions,
    setPermission
  } = useSession();
  const items = [{
    id: 'notifications',
    icon: <BellIcon size={18} data-id="element-3409" data-mp="330cmah6ghrx5" />
  }, {
    id: 'camera',
    icon: <CameraIcon size={18} data-id="element-3410" data-mp="32yt8cx89vsyj" />
  }, {
    id: 'location',
    icon: <MapPinIcon size={18} data-id="element-3411" data-mp="2p0uwbyhfc8m7" />
  }];
  const anyDenied = items.some(item => permissions[item.id] === 'denied');
  return <EntryLayout title={t('onb.perm.title')} body={t('onb.perm.body')} step={5} totalSteps={5} footer={<Button variant="gold" size="lg" fullWidth onClick={() => navigate('/onboarding/primeiro-check')} data-id="element-3413" data-mp="hcq27gitl2cp">
          {t('onb.perm.finish')}
        </Button>} data-id="element-3412" data-mp="2tzoa7s0mi1mw">
      <ul className="space-y-2" data-id="element-3414" data-mp="ma878vtpuxff">
        {items.map(item => {
        const state = permissions[item.id];
        return <li key={item.id} className="rounded-card border border-sand-line bg-white/60 p-2" data-id="element-3415" data-mp="3uqjpgmthbfbs">
              <div className="flex items-start gap-2" data-id="element-3416" data-mp="3pouch1mp4rl2">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-sand-deep text-soft" data-id="element-3417" data-mp="8b8ou5wsrerp">
                  {item.icon}
                </span>
                <div className="min-w-0 flex-1" data-id="element-3418" data-mp="3bz0p6nquafdg">
                  <p className="text-[13.5px] font-medium text-deep" data-id="element-3419" data-mp="3bxplenxe97mc">
                    {t(`onb.perm.${item.id}`)}
                  </p>
                  <p className="mt-[2px] text-[11.5px] leading-snug text-muted" data-id="element-3420" data-mp="1ax2ipb8so015">
                    {t(`onb.perm.${item.id}.hint`)}
                  </p>
                </div>
              </div>

              {state === 'unset' ? <div className="mt-2 flex gap-2" data-id="element-3421" data-mp="1knx5qhtv73nn">
                  <Button variant="outline" fullWidth onClick={() => setPermission(item.id, 'denied')} data-id="element-3422" data-mp="1kp8aokz1qhgc">
                    {t('onb.perm.later')}
                  </Button>
                  <Button variant="primary" fullWidth onClick={() => setPermission(item.id, 'granted')} data-id="element-3423" data-mp="wybb87oftclz">
                    {t('onb.perm.allow')}
                  </Button>
                </div> : <p className={`bee-overline mt-2 text-[8.5px] font-bold ${state === 'granted' ? 'text-soft' : 'text-muted'}`} data-id="element-3424" data-mp="2cd0by35bao2x">
                  {state === 'granted' ? t('onb.perm.granted') : t('onb.perm.denied')}
                </p>}
            </li>;
      })}
      </ul>

      {anyDenied && <p className="mt-3 flex items-start gap-2 rounded-card border border-sand-line bg-sand-deep/60 p-2 text-[11.5px] leading-snug text-deep" data-id="element-3425" data-mp="2hqgwtwt8fx6v">
          <InfoIcon size={14} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-3426" data-mp="2dnkhceyzoh75" />
          {t('onb.perm.deniedNote')}
        </p>}
    </EntryLayout>;
}